/**
 * Begin a query tab's transaction for the statement or results-grid Save
 * about to run — bounded, cancellable, and still owned by that tab when BEGIN
 * comes back.
 *
 * BEGIN is a round trip (a pooled connection is acquired first). While it is
 * on the wire the tab carries `transactionBeginToken`: the toolbar shows Stop
 * instead of Execute, the mode selector is disabled, and closing the tab asks
 * first (Codex r4). The wait is bounded and can be given up:
 *  - `TRANSACTION_BEGIN_DEADLINE_MS` — the pools' own connect/acquire timeout
 *    (30 s on PostgreSQL, MySQL and SQL Server); a BEGIN that has not answered
 *    by then will not, as far as the user is concerned;
 *  - Stop (`cancelTabTransactionBegin`), or the tab being closed.
 * Giving up clears the token at once (the UI is free again), tells main to
 * roll the late session back and release it IN THE BACKGROUND
 * (`abandonTransactionBegin` — main discards the connection if even that
 * hangs), and resolves `abandoned`; nothing is dispatched. A retry after that
 * starts a FRESH attempt (Codex r5).
 *
 * When BEGIN does answer, the tab must still exist and still hold the same
 * token, mode, connection and database; otherwise the session is abandoned
 * the same way and the caller dispatches nothing.
 */
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { isQueryTab, type TransactionIsolationLevel, type TransactionMode } from '../../core/types/workspaceTypes';
import {
  abandonTransactionBegin,
  beginTransaction,
  type TransactionBeginResult,
  type TransactionEngineType,
} from './transactionApi';

/**
 * How long a tab waits for BEGIN: the connect/acquire timeout every pool the
 * transaction engines use is configured with (PG `connectionTimeoutMillis`,
 * MySQL `connectTimeout`, MSSQL `connectTimeout` / `acquireTimeoutMillis`, all
 * 30 000 ms). BEGIN is an acquisition plus one statement.
 */
export const TRANSACTION_BEGIN_DEADLINE_MS = 30_000;

export type TabTransactionBeginRequest = {
  tabId: string;
  connectionId: string;
  database: string;
  engineType: TransactionEngineType;
  isolationLevel?: TransactionIsolationLevel;
  /** The tab's mode when the begin was decided on; a change while BEGIN is in flight voids it. */
  mode: TransactionMode;
};

export type TabTransactionBeginOutcome =
  | { status: 'began'; sessionGeneration?: number }
  /** The backend refused or failed the BEGIN; nothing was acquired. */
  | { status: 'refused'; error?: string }
  /** BEGIN succeeded, but the tab was closed or changed meanwhile; the late session is being released. */
  | { status: 'superseded' }
  /** Given up before BEGIN answered — the deadline passed or the user stopped it. */
  | { status: 'abandoned'; reason: 'timeout' | 'cancelled' };

type Attempt = {
  token: string;
  outcome: Promise<TabTransactionBeginOutcome>;
  cancel: () => void;
  supersede: () => void;
};

const inFlight = new Map<string, Attempt>();
let tokenSequence = 0;

/** Whether a BEGIN is on the wire for this tab (a later caller joins it). */
export function isTabTransactionBeginInFlight(tabId: string): boolean {
  return inFlight.has(tabId);
}

/** Stop: give up on the tab's BEGIN in flight, if any. Returns whether there was one. */
export function cancelTabTransactionBegin(tabId: string): boolean {
  const attempt = inFlight.get(tabId);
  attempt?.cancel();
  return attempt !== undefined;
}

/**
 * A restored tab instance under the same id: a BEGIN still on the wire belongs
 * to the previous instance and has lost ownership. Drop it now (main disposes
 * the late session) instead of letting the new instance wait on it or join it.
 */
export function supersedeStaleTabTransactionBegin(tabId: string): void {
  const attempt = inFlight.get(tabId);
  if (!attempt) return;
  inFlight.delete(tabId);
  attempt.supersede();
}

function tabStillOwns(request: TabTransactionBeginRequest, token: string): boolean {
  const tab = useWorkspaceStore.getState().getTab(request.tabId);
  return (
    !!tab &&
    isQueryTab(tab) &&
    tab.content.transactionBeginToken === token &&
    tab.content.transactionMode === request.mode &&
    tab.connectionInfo?.connectionId === request.connectionId &&
    tab.connectionInfo?.database === request.database
  );
}

function clearToken(tabId: string, token: string): void {
  const { getTab, updateTabContent } = useWorkspaceStore.getState();
  const tab = getTab(tabId);
  if (tab && isQueryTab(tab) && tab.content.transactionBeginToken === token) {
    updateTabContent(tabId, { transactionBeginToken: undefined });
  }
}

type Settled =
  | { kind: 'answered'; result: TransactionBeginResult }
  | { kind: 'timeout' }
  | { kind: 'cancelled' }
  | { kind: 'superseded' };

function start(request: TabTransactionBeginRequest): Attempt {
  const token = `${request.tabId}:${++tokenSequence}`;
  useWorkspaceStore.getState().updateTabContent(request.tabId, { transactionBeginToken: token });

  let cancel = (): void => undefined;
  let supersede = (): void => undefined;
  const cancelled = new Promise<Settled>((resolve) => {
    cancel = () => resolve({ kind: 'cancelled' });
    supersede = () => resolve({ kind: 'superseded' });
  });
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<Settled>((resolve) => {
    timer = setTimeout(() => resolve({ kind: 'timeout' }), TRANSACTION_BEGIN_DEADLINE_MS);
  });
  const answered = beginTransaction(
    request.tabId, request.connectionId, request.database, request.engineType, request.isolationLevel, token,
  ).then((result): Settled => ({ kind: 'answered', result }));

  const outcome = (async (): Promise<TabTransactionBeginOutcome> => {
    try {
      const settled = await Promise.race([answered, timedOut, cancelled]);
      if (settled.kind !== 'answered') {
        abandonTransactionBegin(request.tabId, token);
        if (settled.kind === 'superseded') return { status: 'superseded' };
        return { status: 'abandoned', reason: settled.kind };
      }
      const { result } = settled;
      if (!result.success) return { status: 'refused', error: result.error };
      if (!tabStillOwns(request, token)) {
        // The session exists and nothing owns it: main rolls it back and hands
        // the connection back — in the background, never awaited here.
        abandonTransactionBegin(request.tabId, token);
        return { status: 'superseded' };
      }
      useWorkspaceStore.getState().updateTabContent(request.tabId, {
        transactionState: 'active',
        transactionSessionGeneration: result.sessionGeneration,
        transactionBeginToken: undefined,
      });
      return { status: 'began', sessionGeneration: result.sessionGeneration };
    } finally {
      if (timer) clearTimeout(timer);
      clearToken(request.tabId, token);
      if (inFlight.get(request.tabId)?.token === token) inFlight.delete(request.tabId);
    }
  })();

  return { token, outcome, cancel, supersede };
}

export function beginTabTransaction(request: TabTransactionBeginRequest): Promise<TabTransactionBeginOutcome> {
  const joined = inFlight.get(request.tabId);
  if (joined) return joined.outcome;
  const attempt = start(request);
  inFlight.set(request.tabId, attempt);
  return attempt.outcome;
}
